import { useCallback, useEffect, useMemo, useState } from 'react';
import ProductOptionService from 'services/product_option';
import _ from 'lodash';

const productOptionService = new ProductOptionService();

const useComponent = ({ id, value = [], onChange, onAddNewValue }) => {
  const [option, setOption] = useState({ values: [] });
  const [sortModalOpen, setSortModalOpen] = useState(false);
  const [draftOrder, setDraftOrder] = useState([]);

  const loadData = useCallback(async () => {
    const data = await productOptionService.get(id);
    setOption(data || { values: [] });
  }, [id]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    setDraftOrder(value || []);
  }, [value]);

  const valueLabelMap = useMemo(() => {
    const map = new Map();
    (option.values || []).forEach((item) => {
      map.set(String(item?.id), item?.title || '');
    });
    return map;
  }, [option.values]);

  const normalizeToIds = async (values = []) => {
    const next = [];
    let nextOptionValues = [...(option.values || [])];

    for (let i = 0; i < values.length; i += 1) {
      const raw = values[i];
      const rawStr = String(raw ?? '').trim();
      if (!rawStr) continue;

      const byId = nextOptionValues.find((x) => String(x?.id) === rawStr);
      if (byId) {
        next.push(byId.id);
        continue;
      }

      const byName = nextOptionValues.find((x) => String(x?.title || '').trim().toLowerCase() === rawStr.toLowerCase());
      if (byName) {
        next.push(byName.id);
        continue;
      }

      const newValue = await productOptionService.createValue({ title: rawStr, parent_id: id });
      nextOptionValues = [...nextOptionValues, { id: newValue.id, title: newValue.title }];
      next.push(newValue.id);

      if (onAddNewValue) {
        await onAddNewValue(newValue);
      }
    }

    setOption((prev) => ({ ...prev, values: nextOptionValues }));
    return _.uniq(next);
  };

  const handleSelectChange = async (values) => {
    const nextIds = await normalizeToIds(values);
    onChange(id, nextIds);
  };

  const reorderInDraft = (fromValue, toValue) => {
    const selected = [...(draftOrder || [])];
    const fromIndex = selected.findIndex((x) => String(x) === String(fromValue));
    const toIndex = selected.findIndex((x) => String(x) === String(toValue));
    if (fromIndex < 0 || toIndex < 0 || fromIndex === toIndex) return;

    const [moved] = selected.splice(fromIndex, 1);
    selected.splice(toIndex, 0, moved);
    setDraftOrder(selected);
  };

  const openSortModal = () => {
    setDraftOrder([...(value || [])]);
    setSortModalOpen(true);
  };

  const applySortOrder = () => {
    onChange(id, draftOrder);
    setSortModalOpen(false);
  };

  return {
    id,
    value,
    option,
    valueLabelMap,
    sortModalOpen,
    draftOrder,
    handleSelectChange,
    openSortModal,
    applySortOrder,
    closeSortModal: () => setSortModalOpen(false),
    reorderInDraft,
  };
};

export default useComponent;
